import { CheckboxListField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	NullableStringArrayEqualsFilterModifier,
	NullableStringArrayNotEqualsFilterModifier,
	StringArrayEveryFilterModifier,
	StringArrayNoneFilterModifier,
	StringArraySomeFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { CheckboxListFieldFilterCell } from "./CheckboxListFieldFilterCell"

type CheckboxListFieldValue = string[] | null
type CheckboxListFieldFilterValue = string[] | null

export class CheckboxListFieldFilterDef extends BaseFilterDef<CheckboxListFieldValue, CheckboxListFieldFilterValue> {
	readonly id: string
	readonly defaultFilterValue: CheckboxListFieldFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: CheckboxListField

	constructor(field: CheckboxListField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableStringArrayEqualsFilterModifier,
			isValueValid: (_value: CheckboxListFieldValue): _value is string[] | null => true,
			isFilterValueValid: (_filterValue: CheckboxListFieldFilterValue): _filterValue is string[] | null => true,
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableStringArrayNotEqualsFilterModifier,
			isValueValid: (_value: CheckboxListFieldValue): _value is string[] | null => true,
			isFilterValueValid: (_filterValue: CheckboxListFieldFilterValue): _filterValue is string[] | null => true,
		}),
		every: createFilterModifierConfig({
			modifier: StringArrayEveryFilterModifier,
			isValueValid: (value: CheckboxListFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: CheckboxListFieldFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		some: createFilterModifierConfig({
			modifier: StringArraySomeFilterModifier,
			isValueValid: (value: CheckboxListFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: CheckboxListFieldFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		none: createFilterModifierConfig({
			modifier: StringArrayNoneFilterModifier,
			isValueValid: (value: CheckboxListFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: CheckboxListFieldFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<CheckboxListFieldFilterDef>): ReactNode {
		return <CheckboxListFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
